import React, { useState, Component } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { blogPosts } from '../data/blogData';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Clock, Share2, Facebook, Twitter, Mail, Link as LinkIcon, Check } from 'lucide-react';
export function BlogDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const post = blogPosts.find(p => p.id === id);
  const [copied, setCopied] = useState(false);
  if (!post) {
    return <Navigate to="/blog" replace data-id="element-331" />;
  }
  const relatedPosts = blogPosts.filter(p => p.category === post.category && p.id !== post.id).slice(0, 3);
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post.title;
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${url}`)}`;
        break;
    }
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <Layout data-id="element-332">
      <article data-id="element-333">
        {/* Hero Section */}
        <div className="relative h-[60vh] min-h-[500px] flex items-end pb-16" data-id="element-334">
          <div className="absolute inset-0 z-0" data-id="element-335">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-336" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" data-id="element-337" />
          </div>

          <div className="container-custom relative z-10" data-id="element-338">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6" data-id="element-339">
              <ArrowLeft className="w-4 h-4" data-id="element-340" />
              Back to Blog
            </Link>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-341">
              <span className="inline-block px-3 py-1 bg-[#0A1F44] text-white text-xs font-semibold uppercase tracking-wider rounded-full mb-4" data-id="element-342">
                {post.category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 max-w-4xl" data-id="element-343">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm" data-id="element-344">
                <span className="flex items-center gap-2" data-id="element-345">
                  <User className="w-4 h-4" data-id="element-346" />
                  {post.author}
                </span>
                <span className="flex items-center gap-2" data-id="element-347">
                  <Calendar className="w-4 h-4" data-id="element-348" />
                  {post.date}
                </span>
                <span className="flex items-center gap-2" data-id="element-349">
                  <Clock className="w-4 h-4" data-id="element-350" />
                  {post.readTime}
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container-custom py-16" data-id="element-351">
          <div className="max-w-3xl mx-auto relative" data-id="element-352">
            {/* Share Floating Bar */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} className="hidden lg:flex flex-col gap-4 absolute -left-20 top-0 sticky top-32" data-id="element-353">
              <span className="text-sm font-semibold text-gray-400 uppercase tracking-widest mb-2" style={{
              writingMode: 'vertical-rl'
            }} data-id="element-354">
                Share
              </span>
              <button onClick={() => handleShare('facebook')} className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on Facebook" data-id="element-355">
                <Facebook className="w-5 h-5" data-id="element-356" />
              </button>
              <button onClick={() => handleShare('twitter')} className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share on Twitter" data-id="element-357">
                <Twitter className="w-5 h-5" data-id="element-358" />
              </button>
              <button onClick={() => handleShare('email')} className="w-10 h-10 rounded-full bg-gray-600 text-white flex items-center justify-center hover:opacity-80 transition-opacity" aria-label="Share via Email" data-id="element-359">
                <Mail className="w-5 h-5" data-id="element-360" />
              </button>
              <button onClick={handleCopyLink} className="w-10 h-10 rounded-full bg-[#0A1F44] text-white flex items-center justify-center hover:opacity-80 transition-opacity relative" aria-label="Copy Link" data-id="element-361">
                {copied ? <Check className="w-5 h-5" data-id="element-362" /> : <LinkIcon className="w-5 h-5" data-id="element-363" />}
              </button>
              {copied && <span className="text-sm text-[#0A1F44] font-medium absolute left-12 whitespace-nowrap" data-id="element-364">
                  Link copied!
                </span>}
            </motion.div>

            {/* Article Body */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="prose prose-lg max-w-none prose-headings:font-serif prose-a:text-[#fc0719]" data-id="element-365">
              <p className="lead text-xl text-gray-600 mb-8 font-serif italic" data-id="element-366">
                {post.excerpt}
              </p>
            </motion.div>

            {/* Author Info */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="mt-16 p-8 bg-[#F5F7FA] rounded-xl border-l-4 border-[#fc0719]" data-id="element-367">
              <h3 className="text-xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-368">
                Key Takeaways
              </h3>
              <div className="flex items-start gap-4" data-id="element-369">
                <div className="w-16 h-16 bg-[#0A1F44] rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0" data-id="element-370">
                  {post.author.charAt(0)}
                </div>
                <div data-id="element-371">
                  <p className="text-gray-600 leading-relaxed" data-id="element-372">
                    {post.author} serves as a pastor at TheHousehold of Christ
                    Church, dedicated to teaching God's Word and helping people
                    grow in their faith journey.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Comment Section */}
        <div className="container-custom py-16" data-id="element-373">
          <div className="max-w-3xl mx-auto" data-id="element-374">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100" data-id="element-375">
              <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-376">
                Leave a Comment
              </h3>
              <form className="space-y-6" data-id="element-377">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-378">
                  <div data-id="element-379">
                    <label className="block text-sm font-bold text-[#0A1F44] mb-2 uppercase tracking-wider" data-id="element-380">
                      Name
                    </label>
                    <input type="text" className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#fc0719]" data-id="element-381" />
                  </div>
                  <div data-id="element-382">
                    <label className="block text-sm font-bold text-[#0A1F44] mb-2 uppercase tracking-wider" data-id="element-383">
                      Email
                    </label>
                    <input type="email" className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#fc0719]" data-id="element-384" />
                  </div>
                </div>
                <div data-id="element-385">
                  <label className="block text-sm font-bold text-[#0A1F44] mb-2 uppercase tracking-wider" data-id="element-386">
                    Comment
                  </label>
                  <textarea rows={5} className="w-full px-4 py-3 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#fc0719]" data-id="element-387"></textarea>
                </div>
                <Button className="bg-[#fc0719] hover:bg-[#d4061a] text-white border-none" data-id="element-388">
                  POST COMMENT
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && <section className="py-16 bg-gray-50 border-t" data-id="element-389">
            <div className="container-custom" data-id="element-390">
              <div className="max-w-5xl mx-auto" data-id="element-391">
                <div className="flex items-center justify-between mb-8" data-id="element-392">
                  <h2 className="text-2xl font-serif font-bold text-[#0A1F44]" data-id="element-393">
                    Related Articles
                  </h2>
                  <Link to="/blog" className="text-[#fc0719] font-medium hover:underline" data-id="element-394">
                    View All
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-395">
                  {relatedPosts.map((relatedPost, index) => <motion.article key={relatedPost.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group" data-id="element-396">
                      <Link to={`/blog/${relatedPost.id}`} className="block" data-id="element-397">
                        <div className="relative h-40 overflow-hidden" data-id="element-398">
                          <img src={relatedPost.image} alt={relatedPost.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-399" />
                        </div>
                        <div className="p-5" data-id="element-400">
                          <span className="text-[#0A1F44] text-xs font-semibold uppercase tracking-wider mb-2 block" data-id="element-401">
                            {relatedPost.category}
                          </span>
                          <h3 className="font-serif font-bold text-[#0A1F44] mb-2 line-clamp-2 group-hover:text-[#fc0719] transition-colors" data-id="element-402">
                            {relatedPost.title}
                          </h3>
                          <div className="flex items-center justify-between text-xs text-gray-500" data-id="element-403">
                            <span className="flex items-center gap-1" data-id="element-404">
                              <User className="w-3 h-3" data-id="element-405" />
                              {relatedPost.author}
                            </span>
                            <span className="flex items-center gap-1" data-id="element-406">
                              <Clock className="w-3 h-3" data-id="element-407" />
                              {relatedPost.readTime}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>)}
                </div>
              </div>
            </div>
          </section>}

        {/* Newsletter CTA */}
        <section className="py-16 bg-[#0A1F44]" data-id="element-408">
          <div className="container-custom text-center" data-id="element-409">
            <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-410">
              Stay Connected
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-411">
              Subscribe to our newsletter for weekly devotionals, church
              updates, and encouragement delivered straight to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-412">
              <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#fc0719]" data-id="element-413" />
              <Button className="bg-[#fc0719] hover:bg-[#d4061a] text-white border-none" data-id="element-414">
                Subscribe
              </Button>
            </form>
          </div>
        </section>

        {/* Custom Styles for Article Content */}
        <style dangerouslySetInnerHTML={{
        __html: `
          .prose blockquote {
            border-left-color: #fc0719;
            font-style: italic;
            color: #4A5568;
          }
        `
      }} data-id="element-415" />
      </article>
    </Layout>;
}